import styled, { css, keyframes } from 'styled-components';
import { tokens } from '@/styles/tokens';

const itemIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const StyledNav = styled.nav<{ $variant: 'header' | 'mobile' | 'footer' }>`
  display: flex;
  align-items: center;
  gap: ${tokens.space[6]};

  ${({ $variant }) => {
    switch ($variant) {
      case 'header':
        return css`
          display: flex;

          @media (max-width: 1023px) {
            gap: ${tokens.space[4]};
          }
        `;
      case 'mobile':
        return css`
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: ${tokens.space[2]};
          padding-block: ${tokens.space[6]};
        `;
      case 'footer':
        return css`
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: ${tokens.space[4]} ${tokens.space[6]};
          font-size: ${tokens.fontSizes.sm};
          color: ${tokens.colors.textMuted};

          @media (max-width: 480px) {
            gap: ${tokens.space[2]} ${tokens.space[4]};
          }
        `;
    }
  }}
`;

export const StyledNavItem = styled.a<{ $variant?: 'header' | 'mobile' | 'footer' }>`
  color: ${tokens.colors.textSecondary};
  font-size: ${tokens.fontSizes.label};
  font-weight: ${tokens.fontWeights.medium};
  text-decoration: none;
  transition: color ${tokens.transitions.fast};

  &:hover {
    /* The accent, in both themes, and never an underline.
       specs/022. The hover used to be --color-text-primary, which the header's dark theme overrides to
       white — so on the black band a hovered link went white on black and nothing moved. The underline
       that covered for it was set by a variable in that same theme block, and it is gone.

       The colour now moves in both themes, which is what makes the underline unnecessary rather than
       merely unwanted. It moves to --color-accent, and the header's dark theme overrides that one token:
       #0071E3 is 4.47:1 on black, which is short of AA, and #0A84FF is 3.65:1 on white, which is worse.
       Two values, one behaviour, and both above 4.5:1 where they land. */
    color: var(--color-accent);
  }

  ${({ $variant }) =>
    $variant === 'mobile' &&
    css`
      display: block;
      text-align: left;
      padding: ${tokens.space[3]} 0;
      color: ${tokens.colors.textPrimary};
      font-size: clamp(1.5rem, 5vw, 2rem);
      font-weight: ${tokens.fontWeights.semibold};
      line-height: ${tokens.lineHeights.snug};
      animation: ${itemIn} 360ms var(--ease-out) both;

      &:nth-child(2) { animation-delay: 60ms; }
      &:nth-child(3) { animation-delay: 120ms; }
      &:nth-child(4) { animation-delay: 180ms; }
      &:nth-child(5) { animation-delay: 240ms; }

/* specs/026: touch and keyboard get the same accent the mouse already had. :active is what a
           finger produces — on a touch screen there is no hover to wait for — and :focus-visible is the
           keyboard arriving at an item that was never pointed at. Both resolve through
           var(--color-accent), so the portal-free navigation here follows the menu's theme too. */
        &:hover,
        &:active,
        &:focus-visible {
          color: ${tokens.colors.accent};
        }
      `}

  /* NavLink marks the item that resolves to the current URL with .active and sets
     aria-current="page". Styling that class keeps the current section in sync with the
     router instead of reimplementing route matching here. Kept last so it wins over the
     colour the mobile variant sets, and hover darkens instead of lightening so both
     states keep meeting AA on --color-bg-muted. */
  &.active {
    color: ${tokens.colors.accentText};
  }

  &.active:hover {
    color: ${tokens.colors.accentTextHover};
  }
`;
